import { useEffect, useMemo, useRef, useState } from 'react'
import { dateTimeLabel, isUploadScan } from '../utils/format'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, Github } from 'lucide-react'
import { scansApi } from '../api/scans'
import { githubApi, githubConnectErrorMessage, githubErrorMessage, initiateGithubConnect } from '../api/github'
import { InstallGitHubApp } from '../components/settings/InstallGitHubApp'
import { useAuth } from '../hooks/useAuth'
import type { RepoMetaResponse, ScanCreatedResponse, ScanVisibility } from '../types/api'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/shared/Button'
import { Spinner } from '../components/shared/Spinner'
import { FolderUploadField } from '../components/scans/FolderUploadField'
import { buildPreview, previewProblem, UploadTooLargeError, zipFolder } from '../utils/folderUpload'
import { invalidateScanQueries } from '../hooks/useScanQueries'
import { PageTitle } from '../components/shared/PageTitle'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function repoLabel(url: string): string {
  // DEC-180. Before URL parsing: a folder name may hold spaces, which `new URL` rejects.
  if (isUploadScan(url)) return url.slice('upload://'.length)
  if (url.startsWith('local://')) {
    const path = url.slice('local://'.length)
    const parts = path.split('/').filter(Boolean)
    return parts.length >= 2 ? `…/${parts.slice(-2).join('/')}` : path
  }
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean)
    return parts.slice(0, 2).join('/')
  } catch {
    return url
  }
}

function isValidGithubUrl(url: string): boolean {
  return /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+(\.git)?$/.test(url.trim())
}

/** Mirrors the server's canonical_github_url / branch_from_github_url [DEC-172].
 *
 *  The server is the authority — it normalises for every door a repo_url arrives
 *  through, not just this form. This copy exists so the user sees the cleaned URL
 *  instead of a rejection: `isValidGithubUrl` refuses both a trailing slash and
 *  `github.com/org/repo/tree/main` copied from the address bar, with no explanation,
 *  and the second carries the branch they actually want. */
function normalizeGithubUrl(input: string): { url: string; branch: string | null } {
  const trimmed = input.trim()
  const match = /^https:\/\/github\.com\/(.+)$/i.exec(trimmed)
  if (!match) return { url: trimmed, branch: null }
  const segments = match[1].split('/').filter(Boolean)
  if (segments.length < 2) return { url: trimmed, branch: null }
  const owner = segments[0]
  const repo = segments[1].replace(/\.git$/, '')
  // Everything after `tree/` is the ref, so `feature/x` survives intact.
  const branch =
    segments.length >= 4 && segments[2] === 'tree' ? segments.slice(3).join('/') : null
  return { url: `https://github.com/${owner}/${repo}`, branch }
}

function isValidLocalPath(path: string): boolean {
  return path.trim().startsWith('/')
}


// DEC-180: 'local' is the server-path mode, offered only where the server can read paths.
type SourceMode = 'github-connect' | 'upload' | 'github-url' | 'local'

// ---------------------------------------------------------------------------
// Deferral notice
// ---------------------------------------------------------------------------

/** DEC-126: `run_after` is usually now, but a request folding into an existing
 *  minimum-interval deferral inherits that earlier boundary. Promising "as soon as capacity
 *  frees up" while the scan waits on a clock is the kind of small false statement §0.6 exists
 *  to stop, so the wait is named when it is real. No em-dashes (DEC-069). */
function deferralMessage(result: ScanCreatedResponse): string {
  const base = result.reason ?? 'This scan is queued and will start when capacity frees up.'
  if (!result.run_after) return base
  const when = new Date(result.run_after)
  // A boundary within the next minute is "now" for a reader; do not dress it up as a wait.
  if (Number.isNaN(when.getTime()) || when.getTime() - Date.now() < 60_000) return base
  return `${base} It is queued behind a minimum wait and will start after ${dateTimeLabel(result.run_after)}.`
}

// ---------------------------------------------------------------------------
// GitHub repo picker (DEC-088/089) — browse & pick a repo from the connected
// account instead of pasting a raw URL. Available to any authenticated
// tenant member, not gated to admins (§6.1.2).
// ---------------------------------------------------------------------------

function GithubRepoPicker({
  onPick,
  submitting,
}: {
  onPick: (repoUrl: string, branch: string) => void
  submitting: boolean
}) {
  const { claims } = useAuth()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [changingAccount, setChangingAccount] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['github-repos'],
    queryFn: () => githubApi.listRepos(),
    retry: false,
  })

  const notConnected = isError && error instanceof Error && error.message.toLowerCase().includes('not connected')

  /** DEC-170: disconnect and reconnect, from where the problem is visible.
   *
   *  The capability already existed, in Settings — a different page from the empty list
   *  that tells you you need it. A user staring at a picker with nothing in it should
   *  not have to guess the fix is two screens away. */
  async function onChangeAccount() {
    setChangingAccount(true)
    setConnectError(null)
    try {
      await githubApi.disconnect()
      // Invalidate before reconnecting so a return to this page cannot render the old
      // account's cached repositories as if they were the new account's.
      await queryClient.invalidateQueries({ queryKey: ['github-repos'] })
      await handleConnect()
    } catch (err: unknown) {
      setConnectError(githubConnectErrorMessage(err))
    } finally {
      setChangingAccount(false)
    }
  }

  async function handleConnect() {
    const tenant_id = claims?.tenant_id
    if (!tenant_id) return
    setConnecting(true)
    setConnectError(null)
    try {
      const authorizeUrl = await initiateGithubConnect(tenant_id, window.location.pathname)
      window.location.href = authorizeUrl
    } catch (err: unknown) {
      setConnectError(githubConnectErrorMessage(err))
      setConnecting(false)
    }
  }

  if (notConnected) {
    return (
      <div className="border border-hairline rounded-md px-4 py-6 text-center">
        <Github className="w-6 h-6 text-faint mx-auto mb-2" />
        <p className="text-[13px] text-body mb-3">Connect a GitHub account to browse its repos.</p>
        <Button type="button" size="sm" loading={connecting} onClick={handleConnect}>
          Connect GitHub
        </Button>
        {connectError && <p className="text-[12px] text-critical mt-2">{connectError}</p>}
        {/* DEC-200: the App, beside the OAuth connect. Renders nothing until it is configured. */}
        <InstallGitHubApp className="mt-4 pt-4 border-t border-hairline text-left" />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 flex justify-center">
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <p className="text-[13px] text-critical">Could not load repos from GitHub.</p>
  }

  const allRepos = data?.repos ?? []
  const repos = allRepos.filter((r) =>
    r.full_name.toLowerCase().includes(query.toLowerCase()),
  )

  // DEC-170: three different facts used to render as one sentence, "No matching repos."
  //   - the account owns nothing              -> connect a different account
  //   - the filter excluded everything        -> clear the search
  //   - the account owns things we cannot see -> approve the app in the org
  // The third is the likeliest in a company GitHub org and the least guessable: GitHub
  // returns an empty list for an account that demonstrably owns repositories. Telling
  // that user their search is wrong, when their permissions are, is the worst of the
  // three answers.
  const accountIsEmpty = allRepos.length === 0
  const filterExcludedEverything = allRepos.length > 0 && repos.length === 0

  return (
    <div>
      {/* Naming the account is what makes "use a different account" meaningful. GitHub
          has no prompt=select_account, so reconnecting reuses the signed-in account
          silently and the button looks broken. */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[12px] text-muted truncate">
          {data?.connected_login ? (
            <>
              Connected as <span className="font-medium text-body">{data.connected_login}</span>
            </>
          ) : (
            'GitHub connected'
          )}
        </p>
        <button
          type="button"
          onClick={onChangeAccount}
          disabled={changingAccount}
          className="text-[12px] text-body underline underline-offset-2 hover:text-ink disabled:opacity-50 flex-none"
        >
          {changingAccount ? 'Disconnecting…' : 'Use a different account'}
        </button>
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search repos…"
        disabled={submitting}
        className="w-full border border-control rounded-md px-3 py-2 text-[14px] mb-2 focus:outline-none focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
      />
      <div className="max-h-64 overflow-y-auto border border-hairline rounded-md divide-y divide-hairline">
        {accountIsEmpty ? (
          <div className="text-center py-6 px-4">
            <p className="text-[13px] text-body mb-1">
              This GitHub account has no repositories TDIA can see.
            </p>
            <p className="text-[12px] text-muted">
              If it should have some, your organisation may restrict third-party
              applications: an admin can approve TDIA under the organisation's
              Third-party Access settings. Otherwise, use a different account, or paste a
              public repository link.
            </p>
          </div>
        ) : filterExcludedEverything ? (
          <div className="text-center py-6 px-4">
            <p className="text-[13px] text-body mb-1">
              No repository matches “{query}”.
            </p>
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-[12px] text-body underline underline-offset-2 hover:text-ink"
            >
              Clear the search
            </button>
          </div>
        ) : (
          repos.map((repo) => (
            <button
              key={repo.full_name}
              type="button"
              disabled={submitting}
              onClick={() => onPick(repo.html_url, repo.default_branch)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-hover-hue disabled:opacity-50"
            >
              <span className="text-[14px] text-ink truncate">{repo.full_name}</span>
              {repo.private && (
                <span className="text-[10px] text-faint border border-hairline rounded px-1.5 py-0.5 shrink-0 ml-2">
                  private
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ConnectPage
// ---------------------------------------------------------------------------

export default function ConnectPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [mode, setMode] = useState<SourceMode>('github-connect')

  const [repoUrl, setRepoUrl] = useState('')
  const [branch, setBranch] = useState('main')
  // DEC-176: the repository's own branches, so the form stops guessing `main`. A real
  // scan failed because psf/requests-html uses `master`, and git reported a hard clone
  // failure for a branch that never existed.
  const [repoMeta, setRepoMeta] = useState<RepoMetaResponse | null>(null)
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [localPath, setLocalPath] = useState('')
  // DEC-180: the picked folder. Picking reads the file list and sends nothing.
  const [pickedFiles, setPickedFiles] = useState<File[]>([])
  const [includeGit, setIncludeGit] = useState(true)
  const [uploadPhase, setUploadPhase] = useState<null | 'compressing' | 'uploading'>(null)
  const [uploadFraction, setUploadFraction] = useState(0)
  const uploadAbort = useRef<AbortController | null>(null)
  const { data: sources } = useQuery({
    queryKey: ['scan-sources'],
    queryFn: () => scansApi.sources(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const uploadLimits = sources?.upload ?? null
  const preview = useMemo(
    () => (pickedFiles.length && uploadLimits
      ? buildPreview(pickedFiles, uploadLimits.excluded_dirs, includeGit)
      : null),
    [pickedFiles, uploadLimits, includeGit],
  )
  const modes: SourceMode[] = [
    'github-connect',
    'upload',
    'github-url',
    ...(sources?.local_path_enabled ? (['local'] as SourceMode[]) : []),
  ]
  const [useLlm, setUseLlm] = useState(true)
  // DEC-196: a scan is private to the person who starts it unless they share it.
  const [visibility, setVisibility] = useState<ScanVisibility>('private')
  const workspaceName = useAuth().claims?.tenant_name
  const [sequentialWaves, setSequentialWaves] = useState(false)
  // DEC-094: tiered round-cap extension — off by default.
  const [enableRoundExtension, setEnableRoundExtension] = useState(false)
  const [autoExtensionRounds, setAutoExtensionRounds] = useState('')
  const [approvedExtensionRounds, setApprovedExtensionRounds] = useState('')

  const [error, setError] = useState<string | null>(null)
  // DEC-123: a scan over the concurrency limit is queued rather than refused. Distinct from
  // `error` on purpose: nothing went wrong, and styling it as a failure would misreport it.
  const [deferredNotice, setDeferredNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Post-GitHub-OAuth banner (DEC-090) — the connect button navigates away
  // and the callback redirects back here with a success/error query param.
  const [githubBanner, setGithubBanner] = useState<{ ok: true } | { ok: false; code: string } | null>(null)

  useEffect(() => {
    const connected = searchParams.get('github_connected')
    const errorCode = searchParams.get('github_error')
    if (!connected && !errorCode) return

    if (connected) {
      setGithubBanner({ ok: true })
      setMode('github-connect')
      queryClient.invalidateQueries({ queryKey: ['github-repos'] })
    } else if (errorCode) {
      setGithubBanner({ ok: false, code: errorCode })
      setMode('github-connect')
    }

    const next = new URLSearchParams(searchParams)
    next.delete('github_connected')
    next.delete('github_error')
    setSearchParams(next, { replace: true })
    // Only run once on mount — this is a one-time landing signal, not a
    // reactive binding to searchParams (which we're actively clearing).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // DEC-180: "Upload again" elsewhere in the app links here with ?mode=upload.
  const modeParam = searchParams.get('mode')
  useEffect(() => {
    if (modeParam === 'upload') setMode('upload')
  }, [modeParam])

  // The server-path mode is offered only where the server can read paths. If the server
  // says it cannot, never leave the form on an option that is certain to fail.
  useEffect(() => {
    if (mode === 'local' && sources && !sources.local_path_enabled) setMode('github-connect')
  }, [mode, sources])

  const isValid =
    mode === 'upload'
      ? !!preview && previewProblem(preview, uploadLimits, includeGit) === null
      : mode === 'local'
        ? isValidLocalPath(localPath)
        : isValidGithubUrl(repoUrl)

  async function handleUpload() {
    if (!preview || !uploadLimits) return
    const controller = new AbortController()
    uploadAbort.current = controller
    setSubmitting(true)
    setError(null)
    setDeferredNotice(null)
    setUploadFraction(0)
    try {
      setUploadPhase('compressing')
      const archive = await zipFolder(preview.included, {
        limitBytes: uploadLimits.max_upload_mb * 1024 * 1024,
        onProgress: setUploadFraction,
        signal: controller.signal,
      })
      const form = new FormData()
      form.append('archive', archive, `${preview.folderName}.zip`)
      form.append('folder_name', preview.folderName)
      form.append('include_git', String(includeGit && preview.hasGit))
      form.append('excluded', JSON.stringify(preview.excluded))
      form.append('use_llm', String(useLlm))
      form.append('visibility', visibility)
      form.append('sequential_waves', String(sequentialWaves))
      form.append('enable_round_extension', String(enableRoundExtension))
      if (enableRoundExtension && autoExtensionRounds) form.append('auto_extension_rounds', autoExtensionRounds)
      if (enableRoundExtension && approvedExtensionRounds) form.append('approved_extension_rounds', approvedExtensionRounds)

      setUploadPhase('uploading')
      setUploadFraction(0)
      const result = await scansApi.upload(form, { onProgress: setUploadFraction, signal: controller.signal })
      // DEC-191 decision 5: a new scan changes every list and the sidebar badge.
      void invalidateScanQueries(queryClient)
      if (!result.scan_id) {
        setError('The server accepted the upload but returned no scan to open.')
        return
      }
      navigate(`/scans/${result.scan_id}/monitor`)
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError('Upload cancelled. Nothing was scanned.')
      } else if (err instanceof UploadTooLargeError) {
        setError(`This folder is over ${uploadLimits.max_upload_mb} MB after compression. Leave out more, or scan it from GitHub.`)
      } else {
        setError(err instanceof Error ? err.message : 'The upload failed.')
      }
    } finally {
      uploadAbort.current = null
      setUploadPhase(null)
      setSubmitting(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return
    if (mode === 'upload') {
      await handleUpload()
      return
    }
    setSubmitting(true)
    setError(null)
    setDeferredNotice(null)
    try {
      const finalRepoUrl =
        mode === 'local'
          ? `local://${localPath.trim()}`
          : repoUrl.trim()

      const result = await scansApi.create({
        repo_url: finalRepoUrl,
        branch: mode === 'local' ? 'local' : (branch.trim() || 'main'),
        triggered_by: 'manual',
        use_llm: useLlm,
        visibility,
        sequential_waves: sequentialWaves,
        enable_round_extension: enableRoundExtension,
        auto_extension_rounds: enableRoundExtension && autoExtensionRounds ? Number(autoExtensionRounds) : null,
        approved_extension_rounds: enableRoundExtension && approvedExtensionRounds ? Number(approvedExtensionRounds) : null,
      })
      void invalidateScanQueries(queryClient)
      // DEC-123: over the concurrency limit the scan is queued rather than refused, and it
      // has no id yet. Reported here instead of navigating nowhere.
      if (result.state === 'deferred' || !result.scan_id) {
        setDeferredNotice(deferralMessage(result))
        return
      }
      navigate(`/scans/${result.scan_id}/monitor`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start scan')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppShell crumb="New scan" active="new">
      <main className="flex items-start justify-center pt-16 px-4 pb-16">
        <div className="w-full max-w-3xl">
          <div className="mb-8">
            <PageTitle eyebrow="New scan">Analyze your codebase</PageTitle>
            <p className="text-[14px] text-muted mt-1">
              Connect a repository to start a technical debt analysis.
            </p>
          </div>

          {githubBanner && (
            <div
              className={`mb-6 flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
                githubBanner.ok ? 'border-success/30 bg-chip-success' : 'border-critical/30 bg-chip-critical'
              }`}
            >
              <p className={`text-[13px] ${githubBanner.ok ? 'text-success' : 'text-critical'}`}>
                {githubBanner.ok ? 'GitHub connected.' : githubErrorMessage(githubBanner.code)}
              </p>
              <button
                type="button"
                onClick={() => setGithubBanner(null)}
                className="text-[12px] text-faint hover:text-body flex-none"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Source mode tabs */}
          <div className="flex gap-1 p-1 bg-fill-subtle rounded-lg mb-6 w-fit">
            {modes.map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(null) }}
                className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  mode === m
                    ? 'bg-surface-active text-ink'
                    : 'text-muted hover:text-body'
                }`}
              >
                {m === 'github-connect'
                  ? 'Connect GitHub'
                  : m === 'upload'
                    ? 'Upload a folder'
                    : m === 'local'
                      ? 'Server folder path'
                      : 'Paste a link'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'github-connect' && (
              <div>
                <label className="block text-[13px] font-medium text-body mb-1.5">
                  Pick a repo
                </label>
                <GithubRepoPicker
                  submitting={submitting}
                  onPick={(pickedUrl, pickedBranch) => {
                    setRepoUrl(pickedUrl)
                    setBranch(pickedBranch)
                  }}
                />
                {repoUrl && (
                  <p className="text-[12px] text-muted mt-2">
                    Selected: <span className="font-medium text-body">{repoLabel(repoUrl)}</span> ({branch})
                  </p>
                )}
              </div>
            )}

            {mode === 'github-url' && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="repo-url"
                    className="block text-[13px] font-medium text-body mb-1.5"
                  >
                    Public repository URL
                  </label>
                  <input
                    id="repo-url"
                    type="text"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                    onBlur={async () => {
                      // Normalised on blur, not on every keystroke: rewriting the field
                      // while someone is typing fights them. The server normalises again
                      // regardless; this is so they see the cleaned URL, not a rejection.
                      const { url, branch: fromUrl } = normalizeGithubUrl(repoUrl)
                      if (url !== repoUrl) setRepoUrl(url)
                      if (fromUrl) setBranch(fromUrl)
                      if (!isValidGithubUrl(url)) return

                      // Ask the repository which branches it has [DEC-176]. A failure
                      // here leaves the free-text field exactly as it was: not knowing
                      // must never stop someone starting a scan.
                      setLoadingMeta(true)
                      try {
                        const meta = await githubApi.repoMeta(url)
                        setRepoMeta(meta)
                        // A branch named in the URL is what the user was looking at, so
                        // it wins over the repository's default.
                        if (!fromUrl && meta.default_branch) setBranch(meta.default_branch)
                      } catch {
                        setRepoMeta(null)
                      } finally {
                        setLoadingMeta(false)
                      }
                    }}
                    placeholder="https://github.com/owner/repo"
                    disabled={submitting}
                    className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint tabular-nums focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                  />
                  {repoUrl && !isValidGithubUrl(normalizeGithubUrl(repoUrl).url) && (
                    <p className="text-[12px] text-critical mt-1">
                      Enter a GitHub repository URL, for example
                      https://github.com/owner/repo
                    </p>
                  )}
                  <p className="text-[12px] text-faint mt-1.5">
                    Public repositories only. For a private repository, connect a GitHub
                    account instead.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="repo-branch"
                    className="block text-[13px] font-medium text-body mb-1.5"
                  >
                    Branch
                    {loadingMeta && (
                      <span className="text-[12px] text-faint font-normal ml-2">
                        reading branches…
                      </span>
                    )}
                  </label>
                  {/* A dropdown once the repository has told us its branches, and a
                      free-text field whenever it has not [DEC-176]. Not knowing must
                      never stop someone starting a scan. */}
                  {repoMeta && repoMeta.branches.length > 0 ? (
                    <select
                      id="repo-branch"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      disabled={submitting}
                      className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink tabular-nums focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                    >
                      {/* A branch lifted from a pasted /tree/ URL may not be in a
                          truncated list; keep it selectable rather than silently
                          switching the user to something they did not choose. */}
                      {!repoMeta.branches.includes(branch) && branch && (
                        <option value={branch}>{branch}</option>
                      )}
                      {repoMeta.branches.map((b) => (
                        <option key={b} value={b}>
                          {b}
                          {b === repoMeta.default_branch ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="repo-branch"
                      type="text"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      placeholder="main"
                      disabled={submitting}
                      className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint tabular-nums focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                    />
                  )}
                  {repoMeta?.truncated && (
                    <p className="text-[12px] text-faint mt-1.5">
                      Showing the first {repoMeta.branches.length} branches. If yours is
                      not listed, it still exists.
                    </p>
                  )}
                  {repoMeta?.unavailable_reason && (
                    <p className="text-[12px] text-muted mt-1.5">
                      {repoMeta.unavailable_reason}
                    </p>
                  )}
                </div>
              </div>
            )}

            {mode === 'upload' && (
              <FolderUploadField
                limits={uploadLimits}
                preview={preview}
                includeGit={includeGit}
                disabled={submitting}
                onPick={(files) => { setPickedFiles(files); setError(null) }}
                onIncludeGitChange={setIncludeGit}
              />
            )}

            {mode === 'local' && (
              <div>
                <label className="block text-[13px] font-medium text-body mb-1.5">
                  Absolute path on the server
                </label>
                <input
                  type="text"
                  value={localPath}
                  onChange={(e) => setLocalPath(e.target.value)}
                  placeholder="/Users/you/projects/my-repo"
                  disabled={submitting}
                  className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint tabular-nums focus:outline-none focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
                />
                {localPath && !isValidLocalPath(localPath) && (
                  <p className="text-[12px] text-critical mt-1">
                    Path must be absolute (start with /).
                  </p>
                )}
                <p className="text-[12px] text-faint mt-1.5">
                  The server must have read access to this directory.
                </p>
              </div>
            )}

            {/* Who can see this scan (DEC-196, DEC-198): its own row, the same shape as the
                toggles below, so the source field keeps its full width. Private by default. */}
            <div className="flex items-center justify-between gap-4 py-3 border-t border-hairline" data-testid="visibility-row">
              <div className="min-w-0">
                <p id="visibility-label" className="text-[13px] font-medium text-body">Who can see this scan</p>
                <p className="text-[12px] text-faint mt-0.5">
                  {visibility === 'private'
                    ? `Only you. You can share it with ${workspaceName || 'the workspace'} later.`
                    : `Everyone in ${workspaceName || 'the workspace'} can open it, and it can join their Debt trend.`}
                </p>
              </div>
              <div
                role="radiogroup"
                aria-labelledby="visibility-label"
                className="flex flex-none gap-1 p-1 bg-fill-subtle rounded-lg"
              >
                {(
                  [
                    ['private', 'Only me'],
                    ['workspace', `Everyone in ${workspaceName || 'the workspace'}`],
                  ] as [ScanVisibility, string][]
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={visibility === v}
                    onClick={() => setVisibility(v)}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:shadow-focus ${
                      // Filled accent like a switch that is on, so the chosen option reads at a glance.
                      visibility === v ? 'bg-accent text-white shadow-sm' : 'text-muted hover:text-body'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* LLM toggle */}
            <div className="flex items-center justify-between py-3 border-t border-hairline">
              <div>
                <p className="text-[13px] font-medium text-body">AI enrichment</p>
                <p className="text-[12px] text-faint mt-0.5">
                  {useLlm
                    ? 'LLM enriches CVE context, docstring analysis, and confirms findings'
                    : 'Static analysis only: faster, no API credits used'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUseLlm(v => !v)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:shadow-focus ${
                  useLlm ? 'bg-accent' : 'bg-hairline-strong'
                }`}
                aria-pressed={useLlm}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    useLlm ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Sequential waves toggle — FOR TESTING ONLY */}
            <div className="flex items-center justify-between py-3 border-t border-hairline">
              <div>
                <p className="text-[13px] font-medium text-warn">
                  Sequential mode{' '}
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-chip-warn text-warn px-1.5 py-0.5 rounded ml-1">
                    Testing only
                  </span>
                </p>
                <p className="text-[12px] text-faint mt-0.5">
                  {sequentialWaves
                    ? 'Agents run one at a time: slower but avoids free-tier rate limits'
                    : 'Agents run in parallel waves (production mode)'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSequentialWaves(v => !v)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:outline-none focus-visible:shadow-focus ${
                  sequentialWaves ? 'bg-warn' : 'bg-hairline-strong'
                }`}
                aria-pressed={sequentialWaves}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    sequentialWaves ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Tiered round-cap extension (DEC-094) */}
            <div className="py-3 border-t border-hairline">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] font-medium text-body">Round-cap extension</p>
                  <p className="text-[12px] text-faint mt-0.5">
                    {enableRoundExtension
                      ? 'An agent that runs out of turns gets more automatically, then asks you if it needs even more'
                      : 'Agents stop at their default turn limit and return partial results'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEnableRoundExtension(v => !v)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:shadow-focus ${
                    enableRoundExtension ? 'bg-accent' : 'bg-hairline-strong'
                  }`}
                  aria-pressed={enableRoundExtension}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      enableRoundExtension ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {enableRoundExtension && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label htmlFor="autoExtensionRounds" className="block text-[12px] text-muted mb-1">
                      Auto rounds (1–20)
                    </label>
                    <input
                      id="autoExtensionRounds"
                      type="number"
                      min={1}
                      max={20}
                      value={autoExtensionRounds}
                      onChange={(e) => setAutoExtensionRounds(e.target.value)}
                      placeholder="e.g. 5"
                      className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint focus:outline-none focus:outline-none focus-visible:shadow-focus"
                    />
                  </div>
                  <div>
                    <label htmlFor="approvedExtensionRounds" className="block text-[12px] text-muted mb-1">
                      Rounds if you approve (1–20)
                    </label>
                    <input
                      id="approvedExtensionRounds"
                      type="number"
                      min={1}
                      max={20}
                      value={approvedExtensionRounds}
                      onChange={(e) => setApprovedExtensionRounds(e.target.value)}
                      placeholder="e.g. 10"
                      className="w-full border border-control rounded-md px-3 py-2 text-[14px] text-ink placeholder-faint focus:outline-none focus:outline-none focus-visible:shadow-focus"
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p className="text-[13px] text-critical">{error}</p>
            )}

            {deferredNotice && (
              <div className="flex gap-2 rounded-md border border-warn/30 bg-chip-warn px-3 py-2">
                <Clock className="mt-[2px] h-[13px] w-[13px] flex-none text-warn" />
                <p className="text-[12px] leading-[1.55] text-body">{deferredNotice}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={!isValid}
              loading={submitting}
              className="w-full justify-center py-2.5"
            >
              {uploadPhase === 'compressing'
                ? `Compressing ${Math.round(uploadFraction * 100)}%`
                : uploadPhase === 'uploading'
                  ? `Uploading ${Math.round(uploadFraction * 100)}%`
                  : mode === 'upload'
                    ? 'Scan folder'
                    : mode === 'local'
                      ? 'Scan server folder'
                      : mode === 'github-url'
                        ? 'Scan repository'
                        : 'Connect and scan'}
            </Button>
            {uploadPhase && (
              <button
                type="button"
                onClick={() => uploadAbort.current?.abort()}
                className="w-full text-[12px] text-muted hover:text-ink"
              >
                Cancel upload
              </button>
            )}
          </form>

          {/* DEC-191: Past evaluations moved to the Scans dashboard. */}
          <p className="mt-8 text-center text-[13px] text-muted">
            Every scan, running or finished, is on the{' '}
            <Link to="/scans" className="font-semibold text-accent-text hover:underline">Scans</Link> page.
          </p>
        </div>
      </main>
    </AppShell>
  )
}
